import { motion } from "motion/react";
import { AlertTriangle, Check, FileStack, Hourglass, Loader2, Maximize2, Paperclip, Wrench, XCircle } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import type { AgentState, AgentStatus, Mission, MissionState } from "../mission/engine";
import type { DocWidget } from "../mission/widgets";
import { AskBox, Conversation } from "./Conversation";
import { DocView, WidgetView } from "./Widgets";
import { Elapsed, TONE_COLOR, iconOf, useT } from "./kit";

export const LIVE: AgentStatus[] = ["thinking", "tool", "writing"];

export function StatusChip({ status }: { status: AgentStatus }) {
  const { t } = useTranslation();
  const color = status === "waiting" ? "var(--agx-amber)" : status === "done" ? "var(--agx-green)" : status === "flagged" ? "var(--agx-amber)" : status === "failed" ? "var(--agx-rose)" : status === "queued" ? "var(--agx-faint)" : "var(--sq)";
  const Icon = status === "done" ? Check : status === "flagged" ? AlertTriangle : status === "failed" ? XCircle : status === "waiting" ? Hourglass : status === "tool" ? Wrench : status === "queued" ? null : Loader2;
  return (
    <span className="agx-chip" style={{ ["--c" as string]: color }}>
      {Icon ? <Icon className={`size-2.5 ${status === "thinking" || status === "writing" ? "agx-spin" : ""}`} /> : null}
      {t(`backoffice:agentic.status.${status}`)}
    </span>
  );
}

export function MaximizeButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onClick(); }} className="agx-btn size-6 shrink-0 p-0" data-tone="ghost" aria-label={label} title={label}>
      <Maximize2 className="size-3.5" />
    </button>
  );
}

/**
 * One agent on the canvas: its orb and status, its running conversation (what it thinks, the tools it calls, what it finds, the questions
 * it asks and the officer's answers), its intermediate result, a line to ask it something, and a control to maximise the box.
 */
export function AgentCard({ agent, state, mission, anchor }: { agent: AgentState; state: MissionState; mission: Mission; anchor: (el: HTMLElement | null) => void }) {
  const t = useT();
  const { t: tr } = useTranslation();
  const Icon = iconOf(agent.icon);
  const live = LIVE.includes(agent.status);
  const finished = agent.status === "done" || agent.status === "flagged" || agent.status === "failed";
  const warn = agent.findings.filter((f) => f.tone === "warn" || f.tone === "risk").length;
  const ok = agent.findings.filter((f) => f.tone === "ok").length;
  const open = () => mission.setFocus(agent.id);
  return (
    <motion.div
      layout="position"
      ref={anchor}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      data-squad={agent.squad}
      data-live={live}
      data-status={agent.status}
      className="agx-card h-[352px]"
      onDoubleClick={open}
      aria-label={`${t(agent.name)} · ${tr(`backoffice:agentic.status.${agent.status}`)}`}
    >
      <span className="agx-burst" />
      <div className="agx-card-inner flex flex-col gap-1.5 p-2.5">
        <div className="flex items-start gap-2.5">
          <span className="agx-orb size-7 shrink-0" data-live={live} data-status={agent.status}><Icon className="size-3.5" strokeWidth={2} /></span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold leading-5">{t(agent.name)}</p>
            <p className="truncate text-[11px] agx-dim">{t(agent.role)}</p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <div className="flex items-center gap-1">
              <StatusChip status={agent.status} />
              <MaximizeButton onClick={open} label={tr("backoffice:agentic.maximize")} />
            </div>
            <Elapsed from={agent.startedAt} to={agent.endedAt} className="text-[11px] agx-faint" />
          </div>
        </div>

        {finished && agent.summary ? (
          <p className="-mt-0.5 truncate rounded-md px-2 py-1 text-[11.5px] font-semibold" style={{ color: agent.status === "done" ? "var(--agx-green)" : agent.status === "failed" ? "var(--agx-rose)" : "var(--agx-amber)", background: `color-mix(in oklab, ${agent.status === "done" ? "var(--agx-green)" : agent.status === "failed" ? "var(--agx-rose)" : "var(--agx-amber)"} 10%, transparent)` }}>
            {t(agent.summary)}
          </p>
        ) : null}

        <Conversation agent={agent} state={state} mission={mission} size="card" className="flex-1 pe-0.5" />

        <button type="button" onClick={open} className="h-[84px] shrink-0 overflow-hidden rounded-[5px] border border-[color:var(--agx-line)] agx-wash px-2 py-1.5 text-start agx-hover" aria-label={tr("backoffice:agentic.maximize")}>
          <WidgetView widget={agent.widget} />
        </button>

        <AskBox agentId={agent.id} name={t(agent.name)} size="card" />

        <div className="space-y-1.5">
          <div className="agx-progress" data-live={live}><i style={{ width: `${Math.round(agent.progress * 100)}%` }} /></div>
          <div className="flex items-center gap-3 text-[11px] agx-faint">
            <span className="flex items-center gap-1"><Wrench className="size-3" />{agent.tools.length}</span>
            <span className="flex items-center gap-1"><Paperclip className="size-3" />{agent.sources}</span>
            {ok ? <span className="flex items-center gap-1" style={{ color: "var(--agx-green)" }}><Check className="size-3" />{ok}</span> : null}
            {warn ? <span className="flex items-center gap-1" style={{ color: "var(--agx-amber)" }}><AlertTriangle className="size-3" />{warn}</span> : null}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * The document readers as one squad: one tile per filed document, each its own agent reading in parallel, and their conversations merged
 * into one stream underneath. A tile maximises that reader; the squad's own control maximises the squad.
 */
export function DocumentSquad({ agents, mission, anchor }: { agents: AgentState[]; mission: Mission; anchor: (el: HTMLElement | null) => void }) {
  const t = useT();
  const { t: tr } = useTranslation();
  const live = agents.some((a) => LIVE.includes(a.status));
  const done = agents.filter((a) => a.status === "done" || a.status === "flagged").length;
  const progress = agents.reduce((s, a) => s + a.progress, 0) / Math.max(1, agents.length);
  const flagged = agents.filter((a) => a.status === "flagged").length;
  // The squad's stream: every reader's findings and last steps, newest last.
  const stream = agents
    .flatMap((a) => [...a.findings.map((f) => ({ id: f.id, at: f.at, agent: a, text: f.text, tone: f.tone as "ok" | "info" | "warn" | "risk" | undefined })), ...a.thoughts.slice(-1).map((th) => ({ id: th.id, at: th.at, agent: a, text: th.text, tone: undefined }))])
    .sort((x, y) => x.at - y.at)
    .slice(-14);
  return (
    <motion.div layout="position" ref={anchor} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", stiffness: 200, damping: 22 }}
      data-squad="documents" data-live={live} data-status={done === agents.length ? "done" : "thinking"} className="agx-card h-[352px] col-span-full sm:col-span-2">
      <span className="agx-burst" />
      <div className="agx-card-inner flex flex-col gap-1.5 p-2.5">
        <div className="flex items-center gap-2.5">
          <span className="agx-orb size-[32px] shrink-0" data-live={live}><FileStack className="size-4" strokeWidth={2.2} /></span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold leading-5">{tr("backoffice:agentic.squad.documents")}</p>
            <p className="truncate text-[11px] agx-dim">{tr("backoffice:agentic.squad.documentsRole", { count: agents.length })}</p>
          </div>
          <span className="agx-mono text-[11px]" style={{ color: "var(--sq)" }}>{done}/{agents.length}</span>
          {flagged ? <span className="agx-chip" style={{ ["--c" as string]: "var(--agx-amber)" }}><AlertTriangle className="size-2.5" />{flagged}</span> : null}
          <MaximizeButton onClick={() => mission.setFocus("group:documents")} label={tr("backoffice:agentic.maximize")} />
        </div>
        <div className="grid shrink-0 grid-cols-2 gap-2 sm:grid-cols-4">
          {agents.map((a) => {
            const w = a.widget?.kind === "doc" ? (a.widget as DocWidget) : null;
            const aLive = LIVE.includes(a.status);
            return (
              <button key={a.id} type="button" onClick={() => mission.setFocus(a.id)} data-squad="documents" data-status={a.status}
                className="group relative flex min-w-0 flex-col gap-1 rounded-[5px] border p-1.5 text-start agx-hover"
                style={{ borderColor: a.status === "waiting" ? "var(--agx-amber)" : a.status === "flagged" ? "color-mix(in oklab, var(--agx-amber) 55%, transparent)" : aLive ? "color-mix(in oklab, var(--agx-cyan) 45%, transparent)" : "var(--agx-line)" }}>
                <div className="h-[44px] min-h-0">{w ? <DocView w={w} size="card" /> : <div className="agx-page h-full" />}</div>
                <p className="truncate text-[11px] font-medium" dir="ltr">{a.name.en}</p>
                <p className="flex min-w-0 items-center gap-1 text-[11px]">
                  {a.status === "done" ? <Check className="size-3 shrink-0" style={{ color: "var(--agx-green)" }} /> : a.status === "flagged" ? <AlertTriangle className="size-3 shrink-0" style={{ color: "var(--agx-amber)" }} /> : aLive ? <Loader2 className="size-3 shrink-0 agx-spin" style={{ color: "var(--sq)" }} /> : <Hourglass className="size-3 shrink-0 agx-faint" />}
                  <span className="truncate agx-dim">{a.summary ? t(a.summary) : t(a.role)}</span>
                </p>
              </button>
            );
          })}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto rounded-[5px] border border-[color:var(--agx-line)] agx-sunk p-2">
          <div className="agx-chat">
            {stream.map((e) => (
              <div key={e.id} className="agx-msg" data-from={e.tone ? "finding" : "agent"} style={e.tone ? { ["--tone" as string]: TONE_COLOR[e.tone] } : undefined}>
                <span className="me-1 font-semibold" style={{ color: "var(--sq)" }} dir="ltr">{shortDoc(e.agent.name.en)}</span>{t(e.text)}
              </div>
            ))}
          </div>
        </div>
        <div className="agx-progress" data-live={live}><i style={{ width: `${Math.round(progress * 100)}%` }} /></div>
      </div>
    </motion.div>
  );
}

const shortDoc = (name: string) => name.replace(/\.[a-z0-9]+$/i, "").replace(/^[a-z]+-/, "").slice(0, 28);
